import { AlertCircle, Copy } from "lucide-react";
import { PersonAvatar } from "~/components/PersonAvatar";
import { Alert, AlertDescription } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "~/components/ui/dialog";
import { Skeleton } from "~/components/ui/skeleton";
import { useCopyToClipboard } from "~/pages/manifold-nextgen/hooks/useCopyToClipboard";
import { isEveryoneId } from "~/services/principals";
import { type ShareCollaborators, type ShareUser } from "./types";

export interface OwnersOnlyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resourceLabel: string;
  resourceName?: string;
  // The access-level string that means "owner" for this resource (e.g.
  // ProjectAccessLevel.OWNER, AgentAccessLevel.OWNER, DatasetAccessLevel.OWNER).
  ownerLevel: string;
  collaborators: ShareCollaborators | undefined;
  collaboratorsLoading?: boolean;
  collaboratorsError?: unknown;
}

// Owners can be granted directly or via a group; group members inherit the
// group's access level (see the `toCollaborators`-style helper backing every
// resource's ShareModal), so a plain level filter on each side is enough — no
// rank/effective-role math needed.
const ownerUsers = (
  collaborators: ShareCollaborators | undefined,
  ownerLevel: string
): ShareUser[] => {
  if (!collaborators) {
    return [];
  }
  const direct = collaborators.users.filter(
    (u) => u.access_level === ownerLevel && !isEveryoneId(u.email)
  );
  const viaGroups = collaborators.groups
    .filter((g) => g.access_level === ownerLevel)
    .flatMap((g) => g.users);

  const byEmail = new Map<string, ShareUser>();
  [...direct, ...viaGroups].forEach((u) => byEmail.set(u.email.toLowerCase(), u));
  return [...byEmail.values()].sort((a, b) =>
    (a.full_name || a.email).localeCompare(b.full_name || b.email)
  );
};

// Shown instead of ShareModal for viewers who can't share the resource
// themselves, so they still have a way to reach someone who can grant access.
export const OwnersOnlyDialog = ({
  open,
  onOpenChange,
  resourceLabel,
  resourceName,
  ownerLevel,
  collaborators,
  collaboratorsLoading,
  collaboratorsError,
}: OwnersOnlyDialogProps) => {
  const copyToClipboard = useCopyToClipboard();
  const owners = ownerUsers(collaborators, ownerLevel);
  const resource = resourceLabel.toLowerCase();
  const hasNoOwners = !collaboratorsLoading && !collaboratorsError && owners.length === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gap-3 sm:max-w-[440px]">
        <DialogTitle className="text-xl font-semibold leading-tight tracking-tight">
          Cannot share {resourceLabel}
        </DialogTitle>
        <p className="text-sm text-muted-foreground">
          Only an owner can share {resourceName ? `"${resourceName}"` : `this ${resource}`}.{" "}
          {hasNoOwners
            ? `This ${resource} has no owners.`
            : `Reach out to an owner below to request access or have them share this ${resource} with someone else.`}
        </p>

        <div className="max-h-[300px] overflow-y-auto">
          {collaboratorsLoading ? (
            <div className="flex flex-col gap-3 py-2">
              {Array.from({ length: 2 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3">
                  <Skeleton className="size-[34px] rounded-full" />
                  <Skeleton className="h-4 flex-1 rounded" />
                </div>
              ))}
            </div>
          ) : collaboratorsError ? (
            <Alert variant="destructive">
              <AlertCircle className="size-4" />
              <AlertDescription>
                Failed to load {resource} owners. Please try again.
              </AlertDescription>
            </Alert>
          ) : (
            owners.map((owner) => (
              <div key={owner.email} className="flex items-center gap-3 py-2">
                <PersonAvatar
                  name={owner.full_name || owner.email}
                  email={owner.email}
                  imageUrl={owner.image_url}
                  size={34}
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-foreground">
                    {owner.full_name || owner.email}
                  </p>
                  <p className="truncate text-xs text-muted-foreground">{owner.email}</p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Copy ${owner.full_name || owner.email}'s email`}
                  onClick={() => copyToClipboard(owner.email, "Email")}
                >
                  <Copy className="size-4" />
                </Button>
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
